import { IconButton, type IconButtonProps, Input } from "@/atoms";
import { forwardRef, useMemo, useState } from "react";
import { HexColorPicker } from "react-colorful";

import { useForwardedRef } from "@oneleet/utils/src/hooks";

import * as Popover from "@radix-ui/react-popover";
import { twMerge } from "tailwind-merge";

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const ColorPicker = forwardRef<
  HTMLInputElement,
  Omit<IconButtonProps, "icon" | "value" | "onChange" | "onBlur"> &
    ColorPickerProps
>(
  (
    { value, onChange, onBlur, name, className, disabled, ...props },
    forwardedRef,
  ) => {
    const ref = useForwardedRef(forwardedRef);
    const [open, setOpen] = useState(false);

    const parsedValue = useMemo(() => {
      return value || "#ffffff";
    }, [value]);

    return (
      <Popover.Root onOpenChange={setOpen} open={open}>
        <Popover.Trigger asChild disabled={!!disabled} onBlur={onBlur}>
          <div className="flex items-center gap-2">
            <IconButton
              {...props}
              style={{ backgroundColor: parsedValue }}
              className={twMerge("block", className)}
              name={name}
              onClick={() => {
                setOpen(true);
              }}
              variant="outlined"
              icon={<div className="size-5" />}
              aria-label="Pick a color"
            />
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="justrify-center mt-2 flex w-[216px] flex-col items-center gap-2 rounded-md border border-gray-200 bg-white p-2">
            <HexColorPicker color={parsedValue} onChange={onChange} />
            <Input
              maxLength={7}
              onChange={(e) => {
                onChange(e?.currentTarget?.value);
              }}
              ref={ref}
              value={parsedValue}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  },
);
ColorPicker.displayName = "ColorPicker";

export { ColorPicker };
